import { cn } from '@/lib/utils';
import type { IPortfolioCodeQualityGradeMix } from '@skystone/shared';

interface GradeMixChipRowProps {
  mix: IPortfolioCodeQualityGradeMix;
  className?: string;
}

/**
 * Compact chip row rendering "A 12 · B 4 · C 1 · — 3" for a SonarGrade mix.
 *
 * Per D1 in `decisions.md`, the verdict-strip Reliability and Maintainability
 * tiles use this chip row in place of an invented numeric "score" — it stays
 * in the categorical A–E vocabulary that matches SonarCloud.
 *
 * Chip colours follow the design-system semantic palette (D17):
 *   A → success, B → primary, C → warning, D / E → error, null → muted.
 *
 * Zero-count chips are hidden so the row stays tight ("A 12 · D 1" instead
 * of "A 12 · B 0 · C 0 · D 1 · E 0").
 */
export function GradeMixChipRow({ mix, className }: Readonly<GradeMixChipRowProps>) {
  const chips: Array<{ key: string; label: string; count: number; tone: string }> = [
    { key: 'a', label: 'A', count: mix.a, tone: 'bg-success/15 text-success' },
    { key: 'b', label: 'B', count: mix.b, tone: 'bg-primary/15 text-primary' },
    { key: 'c', label: 'C', count: mix.c, tone: 'bg-warning/15 text-warning' },
    { key: 'd', label: 'D', count: mix.d, tone: 'bg-error/15 text-error' },
    { key: 'e', label: 'E', count: mix.e, tone: 'bg-error/25 text-error' },
    { key: 'null', label: '—', count: mix.nullCount, tone: 'bg-muted text-muted-foreground' },
  ];

  const visible = chips.filter((c) => c.count > 0);
  if (visible.length === 0) {
    return (
      <p className={cn('text-sm text-muted-foreground', className)} data-testid="grade-mix-empty">
        No grades
      </p>
    );
  }

  return (
    <div className={cn('flex flex-wrap items-center gap-1', className)} data-testid="grade-mix-row">
      {visible.map((c, i) => (
        <span key={c.key} className="flex items-center gap-1">
          <span
            className={cn('rounded px-1.5 py-0.5 text-xs font-semibold', c.tone)}
            data-testid={`grade-mix-chip-${c.key}`}
          >
            {c.label} {c.count}
          </span>
          {i < visible.length - 1 && (
            <span aria-hidden="true" className="text-xs text-muted-foreground">·</span>
          )}
        </span>
      ))}
    </div>
  );
}
